import React, { ReactNode, SyntheticEvent, useState } from 'react';
import {
  Form as ReactFinalForm,
  FormProps as ReactFinalFormProps,
  FormRenderProps,
  FormSpy,
} from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, isEqual } from 'es-toolkit/compat';
import PropTypes from 'prop-types';
import { clearToasts, selectToast, showToast } from 'store/redux/toast';

export const FormContext = React.createContext(false);

const defaultErrorMessage = "Let's complete all required fields!";

/** Spread by callers onto whichever element submits the form. */
type NextButtonProps = { onClick: (event: SyntheticEvent) => void };

/**
 * The `...rest` element gives the inferred props an index signature, and React's
 * prop transforms both bail out to the declared props verbatim once
 * `string extends keyof P` — so neither the `propTypes` nor the `defaultProps`
 * below reach a call site, which left `id`, `allowSubmitErrors` and `customError`
 * required at all seven of the checked ones. Declaring the props restores that.
 *
 * The index signature is kept because it is what `...rest` means: whatever is not
 * named here is forwarded verbatim to react-final-form's own `Form`. Typing that
 * remainder as their `FormProps` was tried and reverted — it types `onSubmit`,
 * `validate` and `getFormBottomBar` strictly enough to reject the legacy handlers
 * four existing callers pass, which is a migration rather than a baseline fix.
 */
type FormProps = {
  /**
   * A render prop, called with react-final-form's render props. `propTypes` marks
   * it required, and every caller passes one, but two of them forward an optional
   * of their own — so it is optional here and asserted at the single call below.
   */
  getForm?: (formProps: FormRenderProps) => ReactNode;
  /**
   * Called with `(formProps, nextButtonProps, clearErrors)`. Left as `any` because
   * callers annotate its parameters inconsistently — one of them types the first
   * as `never` — and a signature here would reject them.
   */
  getFormBottomBar?: any;
  id?: string;
  allowSubmitErrors?: boolean;
  onSubmit?: any;
  customError?: string | null;
  [key: string]: any;
};

const Form = ({
  getForm,
  // Mirrors `Form.defaultProps` below, so the unconditional call further down is
  // reachable for a caller that leaves it out.
  getFormBottomBar = () => null,
  id,
  allowSubmitErrors,
  onSubmit,
  customError,
  ...rest
}: FormProps) => {
  const [showErrors, setShowErrors] = useState(false);
  const dispatch = useDispatch();
  const { list: toastList } = useSelector(selectToast);

  const handleSubmit: ReactFinalFormProps['onSubmit'] = async (
    values,
    form,
    callback,
  ) => {
    try {
      dispatch(clearToasts());
      const submissionErrors = await onSubmit(values, form, callback);

      // React final form expects you to return undefined if everything went well or an object with submission errors.
      if (allowSubmitErrors && submissionErrors) {
        dispatch(
          showToast(
            {
              message: submissionErrors?.[0]?.message ?? defaultErrorMessage,
              error: true,
            },
            { autoDelete: true, autoDeleteTime: 4000 },
          ),
        );
        setShowErrors(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const clearError = () => {
    setShowErrors(false);
  };

  return (
    <FormContext.Provider value={showErrors}>
      <ReactFinalForm
        initialValuesEqual={(values1, values2) => isEqual(values1, values2)}
        {...rest}
        onSubmit={handleSubmit}
        render={(formProps) => {
          const { invalid, errors } = formProps;

          const nextButtonProps: NextButtonProps = {
            onClick: (e) => {
              if (invalid) {
                e.preventDefault();
                let errorMessage = defaultErrorMessage;

                // If we have a custom error for one field and that's the only field left, will display a custom error
                // more context here: https://belonghome.atlassian.net/browse/HG-1235
                try {
                  if (customError && errors && typeof errors === 'object') {
                    const keysErrors = errors.keys || {};

                    if (Object.keys(keysErrors).length === 1) {
                      const errorKeys = customError.split('.');
                      const hasChild = errorKeys.length > 1;
                      // `split` always yields at least one element, and the
                      // second is guarded by `hasChild` below.
                      const firstKey = errorKeys[0]!;

                      if (keysErrors[firstKey] !== undefined) {
                        if (
                          hasChild &&
                          typeof keysErrors[firstKey] === 'object'
                        ) {
                          const secondKey = errorKeys[1]!;
                          const nestedError = keysErrors[firstKey][secondKey];
                          if (nestedError !== undefined) {
                            errorMessage = nestedError;
                          }
                        } else {
                          errorMessage = keysErrors[firstKey];
                        }
                      }
                    }
                  }
                } catch (err) {
                  console.warn(
                    'Error while determining validation error message:',
                    err,
                  );
                }

                if (isEmpty(toastList)) {
                  dispatch(
                    showToast(
                      { message: errorMessage, error: true },
                      { autoDelete: true, autoDeleteTime: 4000 },
                    ),
                  );
                }
                setShowErrors(true);
              }
            },
          };

          return (
            <form
              id={id}
              onSubmit={formProps.handleSubmit}
              style={{ position: 'relative' }}
            >
              <FormSpy subscription={{ values: true }} onChange={clearError} />
              {getForm!({ ...formProps })}
              {getFormBottomBar(
                formProps,
                // Spread nextButtonProps on the form submit button.
                // Ex: Advanced.js and IdentityVerification.js
                nextButtonProps,
                () => {
                  setShowErrors(false);
                },
              )}
            </form>
          );
        }}
      />
    </FormContext.Provider>
  );
};

Form.propTypes = {
  getForm: PropTypes.func.isRequired,
  showErrorsState: PropTypes.bool,
  getFormBottomBar: PropTypes.func,
  allowSubmitErrors: PropTypes.bool,
  onSubmit: PropTypes.func,
  customError: PropTypes.string,
  id: PropTypes.string,
};

Form.defaultProps = {
  showErrorsState: false,
  getFormBottomBar: () => {},
};

export default Form;
